// R1c.3a-v3 — Bid Proposal detail with stage history + kickoff.
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import StatusChip from "../../components/StatusChip";
import { toast } from "sonner";
import { ArrowLeft, Download, Rocket, CheckCircle2, XCircle, PauseCircle, Timer, ArrowUpRight } from "lucide-react";

const STATUS_TO_CHIP = {
  draft: "draft", submitted: "connecting", pending_approval: "connecting",
  awarded: "connected", rejected: "error", withdrawn: "not_configured", expired: "degraded",
};

const money = (v) => v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function BidProposalDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [row, setRow] = useState(null);
  const [busy, setBusy] = useState(null);
  const isOwnerOrAdmin = ["OWNER_SUPER_ADMIN", "ADMINISTRATOR"].includes(user?.role_key);

  const load = useCallback(async () => {
    try { setRow((await api.get(`/bid-proposals/${id}`)).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const transition = async (new_status, requiresReason = false) => {
    let reason;
    if (requiresReason) {
      reason = window.prompt(`Reason for transition → ${new_status}?`) || "";
      if (reason === null) return;
    }
    setBusy(new_status);
    try {
      await api.patch(`/bid-proposals/${id}/status`, { new_status, reason });
      toast.success(`Status → ${new_status}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const kickoff = async () => {
    if (!window.confirm("Kick off a project from this awarded proposal? This is audit-logged and creates a project shell.")) return;
    setBusy("kickoff");
    try {
      const r = await api.post(`/bid-proposals/${id}/kickoff`, {});
      toast.success(r.data.already_kicked_off
        ? "Project already exists — navigating to it."
        : "Project shell created from proposal.");
      navigate(`/projects`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const download = async () => {
    try {
      const r = await api.get(`/bid-proposals/${id}/file`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = row?.document?.filename || `bid-proposal-${id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!row) return (
    <AdminAppShell activeNav="crm" title="Bid Proposal" user={user}>
      <div className="py-12 text-center text-erf-dark/60 font-nav" data-testid="bid-proposal-detail-loading">Loading…</div>
    </AdminAppShell>
  );

  const canWithdraw = row.status !== "withdrawn" && row.status !== "rejected"
                    && (isOwnerOrAdmin || row.submitted_by_user_id === user?.id);

  return (
    <AdminAppShell
      activeNav="crm"
      title={row.project_name}
      subtitle={<div className="flex items-center gap-2 mt-1">
        <StatusChip status={STATUS_TO_CHIP[row.status]} testId={`bid-proposal-detail-status-${row.id}`} />
        <span className="text-[11px] font-mono text-erf-dark/60">
          {row.submitted_at ? `submitted ${new Date(row.submitted_at).toLocaleDateString()}` : ""}
        </span>
      </div>}
      breadcrumbs={[
        { label: "CRM", to: "/crm/leads" },
        { label: "Bid Proposals", to: "/crm/bid-proposals" },
        { label: row.project_name, to: null },
      ]}
      user={user}
      actions={
        <Link to="/crm/bid-proposals">
          <Button variant="outline" data-testid="bid-proposal-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </Link>
      }
    >
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 space-y-4">
          <div className="bg-white border border-erf-borderCream rounded-md p-4" data-testid="bid-proposal-meta">
            <div className="grid grid-cols-2 gap-4 text-sm font-nav">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60 mb-0.5">Value</div>
                <div className="font-mono">{money(row.estimated_value)}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60 mb-0.5">Submitted to</div>
                <div className="font-mono text-[11px]">{(row.submitted_to || []).join(", ") || "—"}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60 mb-0.5">Notes</div>
                <div className="text-erf-dark/80 whitespace-pre-wrap">{row.notes || "—"}</div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-erf-borderCream rounded-md p-4" data-testid="bid-proposal-document">
            <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60 mb-2">Document</div>
            {row.document ? (
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-nav font-semibold">{row.document.filename}</div>
                  <div className="text-[11px] font-mono text-erf-dark/60">
                    {((row.document.size || 0) / 1024).toFixed(1)} KB · sha256 {(row.document.sha256 || "").slice(0, 16)}…
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={download} data-testid="bid-proposal-download">
                  <Download className="w-4 h-4 mr-1" /> Download
                </Button>
              </div>
            ) : (
              <div className="text-sm text-erf-dark/60 font-nav">Document row missing — see audit.</div>
            )}
          </div>

          <div className="bg-white border border-erf-borderCream rounded-md p-4" data-testid="bid-proposal-history">
            <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/60 mb-3">Stage history</div>
            <div className="space-y-3">
              {(row.stage_history || []).map((h, i) => (
                <div key={i} className="flex gap-3 text-sm" data-testid={`bid-proposal-history-${i}`}>
                  <div className="w-2 h-2 mt-2 rounded-full bg-erf-gold shrink-0" aria-hidden="true" />
                  <div className="flex-1">
                    <div className="font-nav font-semibold">{h.stage}</div>
                    <div className="text-[11px] font-mono text-erf-dark/60">
                      {new Date(h.at).toLocaleString()} · {h.actor_user_id?.slice(0, 8)}…
                    </div>
                    {h.reason && <div className="text-[12px] text-erf-dark/70 mt-0.5">{h.reason}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-2" data-testid="bid-proposal-actions">
          {row.status === "awarded" && isOwnerOrAdmin && (
            <Button onClick={kickoff} disabled={busy === "kickoff"}
                    className="w-full bg-erf-gold text-erf-dark hover:bg-erf-goldHover"
                    data-testid="bid-proposal-kickoff">
              <Rocket className="w-4 h-4 mr-2" /> Kick off project
            </Button>
          )}

          {["submitted"].includes(row.status) && isOwnerOrAdmin && (
            <Button onClick={() => transition("pending_approval")} disabled={!!busy} variant="outline" className="w-full"
                    data-testid="bid-proposal-pending-approval">
              <PauseCircle className="w-4 h-4 mr-2" /> Move to Pending Approval
            </Button>
          )}
          {["submitted", "pending_approval"].includes(row.status) && isOwnerOrAdmin && (
            <>
              <Button onClick={() => transition("awarded")} disabled={!!busy}
                      className="w-full bg-emerald-700 text-white hover:bg-emerald-800"
                      data-testid="bid-proposal-award">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Awarded
              </Button>
              <Button onClick={() => transition("rejected", true)} disabled={!!busy}
                      variant="outline" className="w-full text-red-700 border-red-300 hover:bg-red-50"
                      data-testid="bid-proposal-reject">
                <XCircle className="w-4 h-4 mr-2" /> Reject
              </Button>
              <Button onClick={() => transition("expired", true)} disabled={!!busy}
                      variant="outline" className="w-full"
                      data-testid="bid-proposal-expire">
                <Timer className="w-4 h-4 mr-2" /> Mark Expired
              </Button>
            </>
          )}
          {canWithdraw && (
            <Button onClick={() => transition("withdrawn", true)} disabled={!!busy}
                    variant="outline" className="w-full"
                    data-testid="bid-proposal-withdraw">
              <ArrowUpRight className="w-4 h-4 mr-2" /> Withdraw
            </Button>
          )}
        </div>
      </div>
    </AdminAppShell>
  );
}
